import { useState, useMemo, useEffect, useRef } from "react";
import {
  LayoutDashboard,
  Activity,
  Wallet,
  Download,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Users,
  Boxes,
  CreditCard,
  AlertTriangle,
  UserCheck,
  LineChart,
  Gauge,
  Lightbulb,
  DollarSign,
  Store,
  Calendar,
  ChevronDown,
  Check,
  X,
  Warehouse,
} from "lucide-react";
import * as XLSX from "xlsx";
import "./AnalyticsReports.css";
import { getAnalyticsDataUrl, getCustomAnalyticsDataUrl } from "../../../api/endpoints";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";
import Spinner from "../../../components/uxCollections/Loaders";

import Template from "../../Template";
import { useSelector } from "react-redux";

// npm install xlsx lucide-react
//
// This component now fetches its own data (it used to take
// transactions/expenses/storeProducts/customers/priceDynamics as props —
// that's gone). It assumes the following already exist in your codebase:
//   getAnalyticsDataUrl        — e.g. "/api/getAnalyticsData/"
//   getCustomAnalyticsDataUrl  — e.g. "/api/getCustomAnalyticsData/"
//   uiAlert, Spinner
// matching the analyticsRouter endpoints: GET /getAnalyticsData/:day and
// GET /getCustomAnalyticsData/:fromDate/:toDate, both accepting an
// optional ?storeID= query param and returning
// { user, transactions, expenses, storeProducts, customers, priceDynamics }.
//
// SCOPE NOTE: Executive Overview, Operational Analytics, Financial Analytics,
// and Downloadable Reports are the four tabs, same as before. Operational
// Analytics now also includes:
//   - Employee metrics (derived from transactions.soldBy — no separate
//     employees prop needed; "voided transactions" and attendance aren't
//     tracked in any schema you've shared, so those two are still omitted)
//   - A sales forecast — this is a plain linear-trend projection over the
//     selected period's daily revenue, computed client-side. It is NOT a
//     real forecasting model (no seasonality, no ML) — label it as a rough
//     projection wherever it's shown, not a guarantee.
//   - A dedicated Credit Performance section (top debtors, overdue list)
//   - A Business Health Score — a rule-based weighted score out of 100
//     (margin, revenue trend, stock availability, credit health, customer
//     retention). It is NOT AI-generated; it's fixed arithmetic you can
//     read and adjust the weights for directly in `healthScore` below.
//   - "Recommendations" — same honesty note: these are deterministic
//     if/else rules reacting to the numbers already computed (dead stock,
//     overdue loans, low margin, etc.), not an LLM call. Wiring this to an
//     actual model (e.g. sending the computed stats to Claude for a real
//     natural-language read) is possible but is a separate integration —
//     say the word if you want that instead of the rule-based version.
//   - A Product Price Dynamics chart, from the fetched priceDynamics data.
//     Note: your PriceChangeSchmema stores `amount` as "the change amount"
//     per your own comment — not the resulting absolute price. There's no
//     starting price anywhere in this schema to reconstruct a real price
//     history from, so the chart plots the change events themselves
//     (magnitude of each adjustment over time, per price kind) rather than
//     a false "price over time" curve built on a guessed baseline.

const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "thisWeek", label: "This Week" },
  { key: "thisMonth", label: "This Month" },
  { key: "thisYear", label: "This Year" },
  { key: "allTime", label: "All Time" },
  { key: "custom", label: "Custom" },
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOUR_BUCKETS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22]; // matches your dashboardSchema's 2-hour buckets
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PAYMENT_MODE_META = {
  cash: { label: "Cash", color: "#1f9d55" },
  card: { label: "Card", color: "#1d6fd8" },
  mobilePay: { label: "Mobile Money", color: "#37b4c5" },
  POS: { label: "POS", color: "#a68b00" },
  bankTransfer: { label: "Bank Transfer", color: "#6b7280" },
  storelensePOS: { label: "StoreLense POS", color: "#8b5cf6" },
};

function getDateRange(preset, customStart, customEnd) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  switch (preset) {
    case "today":
      return { start: startOfDay(now), end: endOfDay(now) };
    case "yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { start: startOfDay(y), end: endOfDay(y) };
    }
    case "thisWeek": {
      const s = new Date(now);
      s.setDate(now.getDate() - now.getDay());
      return { start: startOfDay(s), end: endOfDay(now) };
    }
    case "thisMonth":
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: endOfDay(now) };
    case "thisYear":
      return { start: new Date(now.getFullYear(), 0, 1), end: endOfDay(now) };
    case "custom":
      return {
        start: customStart ? startOfDay(new Date(customStart)) : null,
        end: customEnd ? endOfDay(new Date(customEnd)) : null,
      };
    case "allTime":
    default:
      return { start: null, end: null };
  }
}

function formatMoneyBase(value, currency = "₦") {
  const amount = Number(value) || 0;

  if (amount >= 1_000_000_000_000) {
    return `${currency}${(amount / 1_000_000_000_000).toFixed(amount % 1_000_000_000_000 === 0 ? 0 : 1)}T`;
  }

  if (amount >= 1_000_000_000) {
    return `${currency}${(amount / 1_000_000_000).toFixed(amount % 1_000_000_000 === 0 ? 0 : 1)}B`;
  }

  if (amount >= 1_000_000) {
    return `${currency}${(amount / 1_000_000).toFixed(amount % 1_000_000 === 0 ? 0 : 1)}M`;
  }

  if (amount >= 1_000) {
    return `${currency}${(amount / 1_000).toFixed(amount % 1_000 === 0 ? 0 : 1)}K`;
  }

  return `${currency}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatCompact(value) {
  const v = Math.abs(value || 0);
  if (v >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return Math.round(value || 0).toLocaleString();
}

// ---- small hand-rolled SVG charts (no charting library, matches the rest of the app) ----

const MiniLineChart = ({ points, color = "#1d6fd8", average = null }) => {
  const max = Math.max(1, ...points.map((p) => p.value));
  const w = 640;
  const h = 180;
  const stepX = points.length > 1 ? w / (points.length - 1) : w;
  const coords = points.map((p, i) => [i * stepX, h - (p.value / max) * (h - 20)]);
  const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const averageY = average != null ? h - (average / max) * (h - 20) : null;

  // Only show a handful of evenly-spaced labels — with a long "All Time"
  // range this can be hundreds of points, and a label per point makes an
  // unreadable overlapping smear rather than an axis.
  const maxLabels = 6;
  const labelStep = Math.max(1, Math.ceil(points.length / maxLabels));
  const labelIndices = new Set();
  for (let i = 0; i < points.length; i += labelStep) labelIndices.add(i);
  labelIndices.add(points.length - 1); // always show the last point

  // Same thinning for the dots themselves once there are too many to read individually
  const maxDots = 60;
  const dotStep = Math.max(1, Math.ceil(points.length / maxDots));

  return (
    <svg viewBox={`0 0 ${w} ${h + 24}`} className='sl-ar-chart-svg'>
      {[0, 0.5, 1].map((t) => (
        <line key={t} x1='0' x2={w} y1={h - t * (h - 20)} y2={h - t * (h - 20)} stroke='#edece6' strokeWidth='1' />
      ))}
      {averageY != null && <line x1='0' x2={w} y1={averageY} y2={averageY} stroke={color} strokeWidth='1.5' strokeDasharray='5 4' opacity='0.55' />}
      <path d={path} fill='none' stroke={color} strokeWidth='2.5' />

      {coords.map(([x, y], i) => (i % dotStep === 0 ? <circle key={i} cx={x} cy={y} r='3' fill={color} /> : null))}
      {points.map((p, i) =>
        labelIndices.has(i) ? (
          <text key={p.label + i} x={i * stepX} y={h + 18} fontSize='10.5' fill='#8a8f98' textAnchor='middle' fontFamily="'DM Sans', sans-serif">
            {p.label}
          </text>
        ) : null,
      )}
    </svg>
  );
};

const MiniBarChart = ({ bars, color = "#1d6fd8", showValues = false }) => {
  const max = Math.max(1, ...bars.map((b) => b.value));
  const w = 640;
  const h = 180;
  const topPad = showValues ? 18 : 0;
  const slot = w / bars.length;
  const barW = Math.min(28, slot * 0.5);

  return (
    <svg viewBox={`0 0 ${w} ${h + 24 + topPad}`} className='sl-ar-chart-svg'>
      {[0, 0.5, 1].map((t) => (
        <line key={t} x1='0' x2={w} y1={topPad + h - t * (h - 10)} y2={topPad + h - t * (h - 10)} stroke='#edece6' strokeWidth='1' />
      ))}
      {bars.map((b, i) => {
        const barH = (b.value / max) * (h - 10);
        const x = i * slot + slot / 2 - barW / 2;
        return (
          <g key={b.label}>
            <rect x={x} y={topPad + h - barH} width={barW} height={barH} rx='2' fill={b.color || color} />
            {showValues && (
              <text
                x={i * slot + slot / 2}
                y={topPad + h - barH - 6}
                fontSize='10.5'
                fill='#0f2a47'
                fontWeight='700'
                textAnchor='middle'
                fontFamily="'DM Mono', monospace"
              >
                {formatCompact(b.value)}
              </text>
            )}
            <text x={i * slot + slot / 2} y={topPad + h + 18} fontSize='10.5' fill='#8a8f98' textAnchor='middle' fontFamily="'Nunito', sans-serif">
              {b.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const MiniDonut = ({ segments, currency = "₦" }) => {
  const total = Math.max(
    1,
    segments.reduce((s, seg) => s + seg.value, 0),
  );
  const size = 160;
  const strokeWidth = 20;
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const gap = 8; // px gap rendered between segments along the ring
  const visible = segments.filter((s) => s.value > 0);

  let offsetAccum = 0;
  const arcs = visible.map((seg) => {
    const share = seg.value / total;
    const segLength = Math.max(share * circumference - gap, 0);
    const dashoffset = -offsetAccum;
    offsetAccum += share * circumference;
    return { ...seg, segLength, dashoffset };
  });

  return (
    <div className='sl-ar-donut-wrap'>
      <svg viewBox={`0 0 ${size} ${size}`} className='sl-ar-donut-svg'>
        <circle cx={size / 2} cy={size / 2} r={r} fill='none' stroke='#f0efe9' strokeWidth={strokeWidth} />
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {arcs.map((a) => (
            <circle
              key={a.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill='none'
              stroke={a.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${a.segLength} ${circumference - a.segLength}`}
              strokeDashoffset={a.dashoffset}
              strokeLinecap='round'
            />
          ))}
        </g>
        <text x={size / 2} y={size / 2 - 5} textAnchor='middle' fontFamily="'Nunito', monospace" fontSize='16' fontWeight='700' fill='#0f2a47'>
          {formatCompact(total)}
        </text>
        <text x={size / 2} y={size / 2 + 13} textAnchor='middle' fontFamily="'Nunito', sans-serif" fontSize='9.5' fill='#8a8f98'>
          Total
        </text>
      </svg>
      <div className='sl-ar-donut-legend'>
        {segments.map((s) => (
          <div className='sl-ar-donut-legend-row' key={s.label}>
            <span className='sl-ar-donut-dot' style={{ background: s.color }} />
            <span className='sl-ar-donut-legend-label'>{s.label}</span>
            <span className='sl-ar-donut-legend-pct'>{total > 0 ? Math.round((s.value / total) * 100) : 0}%</span>
            <span className='sl-ar-donut-legend-value'>{formatMoneyBase(s.value, currency)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PRICE_KIND_META = {
  costPrice: { label: "Cost Price", color: "#c23a3a" },
  unitPrice: { label: "Unit Price", color: "#1d6fd8" },
  discountPrice: { label: "Discount", color: "#a68b00" },
};

// Plots each price-change EVENT's magnitude over time, per kind — not a
// reconstructed absolute price curve. See the scope note at the top of this
// file for why (the schema stores deltas, not a starting price).

const PriceDynamicsChart = ({ changes, currency = "₦" }) => {
  const byKind = { costPrice: [], unitPrice: [], discountPrice: [] };
  changes.forEach((c) => {
    if (byKind[c.kind]) byKind[c.kind].push(c);
  });

  const availableKinds = Object.keys(PRICE_KIND_META).filter((k) => byKind[k].length > 0);
  const [selectedKind, setSelectedKind] = useState(availableKinds[0] || "unitPrice");

  useEffect(() => {
    if (availableKinds.length && !availableKinds.includes(selectedKind)) {
      setSelectedKind(availableKinds[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changes]);

  if (!changes.length) {
    return <p className='sl-ar-empty'>No price changes recorded for this product.</p>;
  }

  const toggleRow = (
    <div className='sl-ar-price-toggle'>
      {Object.entries(PRICE_KIND_META).map(([key, meta]) => (
        <button
          key={key}
          className={"sl-ar-price-toggle-btn" + (selectedKind === key ? " sl-ar-price-toggle-btn--active" : "")}
          style={selectedKind === key ? { borderColor: meta.color, color: meta.color } : undefined}
          onClick={() => setSelectedKind(key)}
          disabled={byKind[key].length === 0}
        >
          <i style={{ background: meta.color }} />
          {meta.label}
        </button>
      ))}
    </div>
  );

  const series = [...byKind[selectedKind]].sort((a, b) => new Date(a.changedAt) - new Date(b.changedAt));

  if (series.length === 0) {
    return (
      <>
        {toggleRow}
        <p className='sl-ar-empty'>No {PRICE_KIND_META[selectedKind].label.toLowerCase()} changes recorded for this product.</p>
      </>
    );
  }

  const w = 640;
  const h = 200;
  const chartTop = 10;
  const chartHeight = 150;
  const chartBottom = chartTop + chartHeight;
  const startX = 62;
  const endX = w - 10;

  const values = series.map((c) => c.amount || 0);
  const maxVal = Math.max(...values);
  const minVal = Math.min(0, ...values);
  const range = Math.max(1, maxVal - minVal);

  const dates = series.map((c) => new Date(c.changedAt).getTime());
  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const dateSpan = Math.max(1, maxDate - minDate);

  const toXY = (c) => {
    const x = series.length > 1 ? startX + ((new Date(c.changedAt).getTime() - minDate) / dateSpan) * (endX - startX) : (startX + endX) / 2;
    const y = chartBottom - ((c.amount - minVal) / range) * chartHeight;
    return [x, y];
  };

  const coords = series.map(toXY);
  const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const meta = PRICE_KIND_META[selectedKind];

  const maxLabels = 6;
  const labelStep = Math.max(1, Math.ceil(series.length / maxLabels));

  return (
    <>
      {toggleRow}
      <svg viewBox={`0 0 ${w} ${h}`} className='sl-ar-chart-svg'>
        {[0, 0.5, 1].map((t) => {
          const y = chartBottom - t * chartHeight;
          const value = minVal + t * range;
          return (
            <g key={t}>
              <line x1={startX} x2={endX} y1={y} y2={y} stroke='#edece6' strokeWidth='1' />
              <text x={startX - 8} y={y + 3} fontSize='10' fill='#8a8f98' textAnchor='end' fontFamily="'Nunito', monospace">
                {formatMoneyBase(value, currency)}
              </text>
            </g>
          );
        })}
        <line x1={startX} x2={startX} y1={chartTop} y2={chartBottom} stroke='#c9c7bd' strokeWidth='1' />
        <line x1={startX} x2={endX} y1={chartBottom} y2={chartBottom} stroke='#c9c7bd' strokeWidth='1' />

        <path d={path} fill='none' stroke={meta.color} strokeWidth='2.5' />
        {coords.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r='3.5' fill={meta.color} />
        ))}

        {series.map((c, i) => {
          if (i % labelStep !== 0 && i !== series.length - 1) return null;
          const [x] = coords[i];
          const label = new Date(c.changedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
          return (
            <text key={i} x={x} y={h - 6} fontSize='10' fill='#8a8f98' textAnchor='middle' fontFamily="'Nunito', sans-serif">
              {label}
            </text>
          );
        })}
      </svg>
    </>
  );
};

// standalone so its own open/close state and outside-click ref don't
// collide with anything else on the page
const StoreDropdown = ({ storeFilter, setStoreFilter, availableStores }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = storeFilter === "all" ? "All Stores" : availableStores.find((s) => s.id === storeFilter)?.name || "All Stores";

  return (
    <div className='sl-ar-store-dropdown' ref={ref}>
      <button type='button' className={"sl-ar-store-trigger" + (open ? " sl-ar-store-trigger--open" : "")} onClick={() => setOpen((o) => !o)}>
        <Store size={14} />
        <span>{selectedLabel}</span>
        <ChevronDown size={13} className='sl-ar-store-chevron' />
      </button>

      {open && (
        <div className='sl-ar-store-menu'>
          <button
            type='button'
            className={"sl-ar-store-option" + (storeFilter === "all" ? " sl-ar-store-option--active" : "")}
            onClick={() => {
              setStoreFilter("all");
              setOpen(false);
            }}
          >
            <Check size={13} className='sl-ar-store-check' />
            <span>All Stores</span>
          </button>
          {availableStores.map((s) => (
            <button
              type='button'
              key={s.id}
              className={"sl-ar-store-option" + (storeFilter === s.id ? " sl-ar-store-option--active" : "")}
              onClick={() => {
                setStoreFilter(s.id);
                setOpen(false);
              }}
            >
              <Check size={13} className='sl-ar-store-check' />
              <span>{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DataCard = ({ title, badge, rows }) => (
  <div className='sl-ar-data-card'>
    <div className='sl-ar-data-card-top'>
      <span className='sl-ar-data-card-title'>{title}</span>
      {badge != null && <span className='sl-ar-data-card-badge'>{badge}</span>}
    </div>
    {rows.map(([label, value]) => (
      <div className='sl-ar-data-card-row' key={label}>
        <span>{label}</span>
        <b>{value}</b>
      </div>
    ))}
  </div>
);

export const AnalyticsReports = () => {
  const { user } = useSelector((state) => state.webStoreUser);
  const currency = user.userCountryCurrencySymbol || "₦";

  const formatMoney = (value) => formatMoneyBase(value, currency);

  const [transactions, setTransactions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [storeProducts, setStoreProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [priceDynamics, setPriceDynamics] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseProducts, setWarehouseProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState("Fetching analytics data");

  const [activeTab, setActiveTab] = useState("executive");
  const [datePreset, setDatePreset] = useState("thisMonth");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [storeFilter, setStoreFilter] = useState("all");
  const [isTimelineSheetOpen, setIsTimelineSheetOpen] = useState(false);
  const [isReportSheetOpen, setIsReportSheetOpen] = useState(false);
  const [isInsightsSheetOpen, setIsInsightsSheetOpen] = useState(false);

  // The store list has to survive a store-filtered fetch (otherwise picking
  // "Ikeja Store" would wipe every other store out of its own dropdown) —
  // only ever refreshed from an unfiltered ("All Stores") response.
  const [availableStores, setAvailableStores] = useState([]);

  const applyFetchedData = (payload, requestedStoreID) => {
    setTransactions(payload.transactions || []);
    setExpenses(payload.expenses || []);
    setStoreProducts(payload.storeProducts || []);
    setCustomers(payload.customers || []);

    setPriceDynamics(payload.priceDynamics || []);
    setWarehouses(payload.warehouses || []);
    setWarehouseProducts(payload.warehouseProducts || []);

    // only refresh the store list from an unfiltered ("All Stores") response —
    // a store-scoped response would otherwise wipe every other store out of
    // its own dropdown
    if (!requestedStoreID) {
      const map = new Map();
      (payload.transactions || []).forEach((t) => {
        if (t.store?.id) map.set(String(t.store.id), t.store.name);
      });
      setAvailableStores([...map.entries()].map(([id, name]) => ({ id, name })));
    }
  };

  const fetchAnalyticsData = async (day) => {
    setIsLoading(true);
    setLoadingProgress("Fetching analytics data");
    try {
      const requestedStoreID = storeFilter !== "all" ? storeFilter : null;
      const query = requestedStoreID ? `?storeID=${requestedStoreID}` : "";
      const response = await fetch(getAnalyticsDataUrl + day + query, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });

      if (response.ok) {
        const payload = await response.json();
        applyFetchedData(payload, requestedStoreID);
      } else {
        uiAlert("something went wrong", "error");
      }
    } catch (error) {
      console.log(error);
      uiAlert("something went wrong", "error");
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const fetchCustomAnalyticsData = async (fromDate, toDate) => {
    setIsLoading(true);
    setLoadingProgress("Fetching analytics data");
    try {
      const requestedStoreID = storeFilter !== "all" ? storeFilter : null;
      const query = requestedStoreID ? `?storeID=${requestedStoreID}` : "";
      const response = await fetch(getCustomAnalyticsDataUrl + `${fromDate}/${toDate}` + query, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });

      if (response.ok) {
        const payload = await response.json();
        applyFetchedData(payload, requestedStoreID);
      } else {
        uiAlert("something went wrong", "error");
      }
    } catch (error) {
      console.log(error);
      uiAlert("something went wrong", "error");
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  // refetch whenever the timeline preset, custom range, or store filter changes.
  // "custom" waits for both ends of the range before firing.
  useEffect(() => {
    if (datePreset === "custom") {
      if (customStart && customEnd) fetchCustomAnalyticsData(customStart, customEnd);
      return;
    }
    const label = DATE_PRESETS.find((p) => p.key === datePreset)?.label;
    if (label) fetchAnalyticsData(label);
  }, [datePreset, customStart, customEnd, storeFilter]);

  const filteredStoreProducts = useMemo(
    () => (storeFilter === "all" ? storeProducts : storeProducts.filter((p) => String(p.storeID) === storeFilter)),
    [storeProducts, storeFilter],
  );

  const filteredCustomers = useMemo(
    () => (storeFilter === "all" ? customers : customers.filter((c) => String(c.storepurchasedFrom?.storeID) === storeFilter)),
    [customers, storeFilter],
  );

  const { start, end } = useMemo(() => getDateRange(datePreset, customStart, customEnd), [datePreset, customStart, customEnd]);

  // used only for the "new customers this period" check below — customers
  // are a snapshot, not filtered by the backend the way transactions/expenses are
  const inRange = (date) => {
    if (!date) return false;
    const d = new Date(date);
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };

  // the backend already scopes these to the selected timeline + store
  const periodTransactions = transactions;
  const periodExpenses = expenses;

  // ---------------- shared derived figures ----------------

  /*  const revenue = useMemo(
    () => periodTransactions.reduce((sum, t) => sum + ((t.transactionDetails?.subTotal || 0) - t.transactionDetails?.discount), 0),
    [periodTransactions]
  ); */

  //helpers
  const isReturnedTransaction = (t) => t.transactionDetails?.deliveryStatus === "returned";

  const getTransactionRevenue = (t) => {
    if (isReturnedTransaction(t)) return 0;

    return (t.transactionDetails?.subTotal || 0) - (t.transactionDetails?.discount || 0);
  };

  const getTransactionCOGS = (t) => {
    return (t.products || []).reduce((sum, p) => sum + (p.productCostPrice || 0) * (p.quantity || 0), 0);
  };

  const getTransactionProfit = (t) => {
    if (isReturnedTransaction(t)) return 0;

    return getTransactionRevenue(t) - getTransactionCOGS(t);
  };

  //revenue

  /* const revenue = useMemo(
    () =>
      periodTransactions.reduce((sum, t) => {
        if (t.transactionDetails?.deliveryStatus === "returned") return sum;

        return sum + ((t.transactionDetails?.subTotal || 0) - (t.transactionDetails?.discount || 0));
      }, 0),
    [periodTransactions],
  ); */

  const revenue = useMemo(() => periodTransactions.reduce((sum, t) => sum + getTransactionRevenue(t), 0), [periodTransactions]);

  /*   const cogs = useMemo(
    () => periodTransactions.reduce((sum, t) => sum + (t.products || []).reduce((s, p) => s + (p.productCostPrice || 0) * (p.quantity || 0), 0), 0),
    [periodTransactions]
  );
 */
  /*   const cogs = useMemo(
    () =>
      periodTransactions.reduce((sum, t) => {
        if (t.transactionDetails?.deliveryStatus === "returned") return sum;

        return sum + (t.products || []).reduce((s, p) => s + (p.productCostPrice || 0) * (p.quantity || 0), 0);
      }, 0),
    [periodTransactions],
  ); */

  const cogs = useMemo(
    () =>
      periodTransactions.reduce((sum, t) => {
        if (isReturnedTransaction(t)) return sum;

        return sum + getTransactionCOGS(t);
      }, 0),
    [periodTransactions],
  );

  const grossProfit = revenue - cogs;
  const grossMargin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  const totalExpenses = useMemo(() => periodExpenses.reduce((s, e) => s + (e.amount || 0), 0), [periodExpenses]);
  const netProfit = grossProfit - totalExpenses;

  // group by full ISO date (not "15 Jan") so multi-year ranges like "All Time"
  // don't silently merge e.g. Jan 15 2024 and Jan 15 2025 into one bucket,
  // and sort chronologically since Map iteration order is just insertion order
  const buildDailyTrend = (valueFn) => {
    const byDay = new Map();
    periodTransactions.forEach((t) => {
      const d = new Date(t.transactionDetails?.date);
      if (isNaN(d)) return;
      const key = d.toISOString().split("T")[0]; // YYYY-MM-DD, collision-proof
      byDay.set(key, (byDay.get(key) || 0) + valueFn(t));
    });

    const sortedKeys = [...byDay.keys()].sort();
    const spansMultipleYears = sortedKeys.length > 0 && sortedKeys[0].slice(0, 4) !== sortedKeys[sortedKeys.length - 1].slice(0, 4);

    return sortedKeys.map((key) => {
      const d = new Date(key);
      const label = d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        ...(spansMultipleYears ? { year: "2-digit" } : {}),
      });
      return { label, value: byDay.get(key) };
    });
  };

  /*  const revenueTrend = useMemo(
    () =>
      buildDailyTrend((t) => {
        if (t.transactionDetails?.deliveryStatus === "returned") return 0;
        return t.transactionDetails?.totalAmount || 0;
      }),
    [periodTransactions],
  ); */

  /*   const revenueTrend = useMemo(
    () =>
      buildDailyTrend((t) => {
        if (t.transactionDetails?.deliveryStatus === "returned") return 0;

        return (t.transactionDetails?.subTotal || 0) - (t.transactionDetails?.discount || 0);
      }),
    [periodTransactions],
  ); */

  const revenueTrend = useMemo(() => buildDailyTrend((t) => getTransactionRevenue(t)), [periodTransactions]);
  /* 
  const profitTrend = useMemo(
    () =>
      buildDailyTrend((t) => {
        const lineCogs = (t.products || []).reduce((s, p) => s + (p.productCostPrice || 0) * (p.quantity || 0), 0);
        return (t.transactionDetails?.totalAmount || 0) - lineCogs;
      }),
    [periodTransactions],
  ); */

  /*   const profitTrend = useMemo(
    () =>
      buildDailyTrend((t) => {
        if (t.transactionDetails?.deliveryStatus === "returned") return 0;

        const revenue = (t.transactionDetails?.subTotal || 0) - (t.transactionDetails?.discount || 0);

        const lineCogs = (t.products || []).reduce((s, p) => s + (p.productCostPrice || 0) * (p.quantity || 0), 0);

        return revenue - lineCogs;
      }),
    [periodTransactions],
  ); */

  const profitTrend = useMemo(() => buildDailyTrend((t) => getTransactionProfit(t)), [periodTransactions]);

  // number of days in the selected period, used for averages and per-day figures
  // ignore the time portion so e.g. 2024-01-01 23:59 to 2024-01-02 00:01 counts as 2 days, not 1

  const periodDays = useMemo(() => {
    if (datePreset === "allTime") {
      if (!periodTransactions.length) return 1;

      const earliest = periodTransactions.reduce((min, t) => {
        const d = new Date(t.transactionDetails?.date);
        return d < min ? d : min;
      }, new Date(periodTransactions[0].transactionDetails?.date));

      earliest.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return Math.floor((today - earliest) / (1000 * 60 * 60 * 24)) + 1;
    }

    if (!start || !end) return 1;

    // Ignore the time portion
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(end);
    endDate.setHours(0, 0, 0, 0);

    return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  }, [start, end]);

  /*  const avgRevenue = useMemo(() => (revenueTrend.length ? revenueTrend.reduce((s, p) => s + p.value, 0) / revenueTrend.length : 0), [revenueTrend]);

  const avgProfit = useMemo(() => (profitTrend.length ? profitTrend.reduce((s, p) => s + p.value, 0) / profitTrend.length : 0), [profitTrend]); */

  const avgRevenue = useMemo(() => revenue / periodDays, [revenue, periodDays]);

  const avgProfit = useMemo(() => grossProfit / periodDays, [grossProfit, periodDays]);

  /*   const cashFlowSegments = useMemo(() => {
    const byMode = { cash: 0, card: 0, mobilePay: 0, POS: 0, bankTransfer: 0 };
    let creditSales = 0;
    periodTransactions.forEach((t) => {
      const mode = t.transactionDetails?.paymentMode;
      if (byMode[mode] !== undefined) byMode[mode] += t.transactionDetails?.totalAmount || 0;
      if (t.operationType?.whichOperation === "loan") creditSales += t.transactionDetails?.totalAmount || 0;
    });
    const segs = Object.entries(byMode).map(([key, value]) => ({ label: PAYMENT_MODE_META[key].label, value, color: PAYMENT_MODE_META[key].color }));
    if (creditSales > 0) segs.push({ label: "Credit Sales", color: "#c23a3a", value: creditSales });
    return segs;
  }, [periodTransactions]); */

  /*  const cashFlowSegments = useMemo(() => {
    const byMode = {
      cash: 0,
      card: 0,
      mobilePay: 0,
      POS: 0,
      bankTransfer: 0,
    };

    let creditSales = 0;

    periodTransactions.forEach((t) => {
      if (isReturnedTransaction(t)) return;

      const revenue = getTransactionRevenue(t);

      const mode = t.transactionDetails?.paymentMode;

      if (byMode[mode] !== undefined) byMode[mode] += revenue;

      if (t.operationType?.whichOperation === "loan") creditSales += revenue;
    });

    const segs = Object.entries(byMode).map(([key, value]) => ({
      label: PAYMENT_MODE_META[key].label,
      value,
      color: PAYMENT_MODE_META[key].color,
    }));

    if (creditSales > 0)
      segs.push({
        label: "Credit Sales",
        color: "#c23a3a",
        value: creditSales,
      });

    return segs;
  }, [periodTransactions]); */
  const cashFlowSegments = useMemo(() => {
    const byMode = {
      cash: 0,
      card: 0,
      mobilePay: 0,
      POS: 0,
      bankTransfer: 0,
      storelensePOS: 0,
    };

    let creditSales = 0;

    periodTransactions.forEach((t) => {
      if (isReturnedTransaction(t)) return;

      const revenue = getTransactionRevenue(t);
      const td = t.transactionDetails || {};

      if (td.paymentMode === "split" && Array.isArray(td.payments) && td.payments.length) {
        // "split" isn't a real payment channel — it never matched a byMode
        // key before, so a split transaction's revenue was silently
        // dropped from the chart entirely. Decompose it into each
        // account's actual amount instead.
        td.payments.forEach((p) => {
          const key = byMode[p.paymentType] !== undefined ? p.paymentType : "cash";
          byMode[key] += Number(p.amount) || 0;
        });
      } else {
        const key = byMode[td.paymentMode] !== undefined ? td.paymentMode : "cash";
        byMode[key] += revenue;
      }

      if (t.operationType?.whichOperation === "loan") creditSales += revenue;
    });

    const segs = Object.entries(byMode).map(([key, value]) => ({
      label: PAYMENT_MODE_META[key].label,
      value,
      color: PAYMENT_MODE_META[key].color,
    }));

    if (creditSales > 0) segs.push({ label: "Credit Sales", color: "#c23a3a", value: creditSales });

    return segs;
  }, [periodTransactions]);
  /* 
  const orderStats = useMemo(() => {
    const amounts = periodTransactions.map((t) => t.transactionDetails?.totalAmount || 0);
    const returned = periodTransactions.filter((t) => t.transactionDetails?.deliveryStatus === "returned").length;
    return {
      count: periodTransactions.length,
      aov: periodTransactions.length ? revenue / periodTransactions.length : 0,
      highest: amounts.length ? Math.max(...amounts) : 0,
      lowest: amounts.length ? Math.min(...amounts) : 0,
      returned,
    };
  }, [periodTransactions, revenue]); */

  const orderStats = useMemo(() => {
    const completed = periodTransactions.filter((t) => !isReturnedTransaction(t));

    const amounts = completed.map((t) => getTransactionRevenue(t));

    return {
      count: completed.length,

      aov: completed.length ? revenue / completed.length : 0,

      highest: amounts.length ? Math.max(...amounts) : 0,

      lowest: amounts.length ? Math.min(...amounts) : 0,

      returned: periodTransactions.length - completed.length,
    };
  }, [periodTransactions, revenue]);

  const customerStats = useMemo(() => {
    const newCustomers = filteredCustomers.filter((c) => c.isNewCustomer && inRange(c.createdAt)).length;
    const returning = filteredCustomers.filter((c) => !c.isNewCustomer).length;
    const active = filteredCustomers.filter((c) => c.status === "active").length;
    const owing = filteredCustomers.filter((c) => (c.amountOwned || 0) > 0);
    const top = [...filteredCustomers].sort((a, b) => (b.totalPurchaseAmount || 0) - (a.totalPurchaseAmount || 0))[0];
    return {
      newCustomers,
      returning,
      active,
      owingCount: owing.length,
      owingTotal: owing.reduce((s, c) => s + (c.amountOwned || 0), 0),
      topCustomerName: top?.fullName || "—",
      topCustomerAmount: top?.totalPurchaseAmount || 0,
    };
  }, [filteredCustomers, start, end]);

  const inventoryStats = useMemo(() => {
    const valueCost = filteredStoreProducts.reduce((s, p) => s + (p.costPrice || 0) * (p.quantity || 0), 0);
    const valueSelling = filteredStoreProducts.reduce((s, p) => s + (p.unitPrice || 0) * (p.quantity || 0), 0);
    const lowStock = filteredStoreProducts.filter((p) => (p.quantity || 0) > 0 && p.stockLimit && p.quantity <= p.stockLimit).length;
    const outOfStock = filteredStoreProducts.filter((p) => (p.quantity || 0) === 0).length;
    // heuristic: more than double the stock limit — flagged as an assumption, adjust the multiplier to whatever "overstocked" means for your business
    const overstocked = filteredStoreProducts.filter((p) => p.stockLimit && p.quantity > p.stockLimit * 2).length;
    return { valueCost, valueSelling, count: filteredStoreProducts.length, lowStock, outOfStock, overstocked };
  }, [filteredStoreProducts]);

  const warehouseStats = useMemo(() => {
    const valueCost = warehouseProducts.reduce((s, p) => s + (p.costPrice || 0) * (p.quantity || 0), 0);
    const totalUnits = warehouseProducts.reduce((s, p) => s + (p.quantity || 0), 0);
    const lowStock = warehouseProducts.filter((p) => (p.quantity || 0) > 0 && p.stockLimit && p.quantity <= p.stockLimit).length;
    const outOfStock = warehouseProducts.filter((p) => (p.quantity || 0) === 0).length;
    const damagedUnits = warehouseProducts.reduce((s, p) => s + (p.damagedAmount || 0), 0);
    const movedToStores = warehouseProducts.filter((p) => (p.storesMovedTo || []).length > 0).length;
    return {
      warehouseCount: warehouses.length,
      productCount: warehouseProducts.length,
      totalUnits,
      valueCost,
      lowStock,
      outOfStock,
      damagedUnits,
      movedToStores,
    };
  }, [warehouses, warehouseProducts]);

  const creditStats = useMemo(() => {
    const outstandingDebt = filteredCustomers.reduce((s, c) => s + (c.amountOwned || 0), 0);
    const loanTransactions = periodTransactions.filter((t) => t.operationType?.whichOperation === "loan");

    const recovered = loanTransactions.reduce((s, t) => s + (t.operationType?.clearedAmount || 0), 0);

    const overdue = loanTransactions.filter((t) => {
      const dueDate = t.operationType?.dateToPay ? new Date(t.operationType.dateToPay) : null;

      return dueDate && dueDate < new Date() && (t.operationType?.balance || 0) > 0;
    }).length;
    // repayment time needs loanDate -> clearedDate; both are stored inconsistently (Date vs String) in
    // your schema, so this is a best-effort average over the ones that parse cleanly
    const repaymentDurations = loanTransactions
      .filter((t) => t.operationType?.loanDate && t.operationType?.clearedDate)
      .map((t) => (new Date(t.operationType.clearedDate) - new Date(t.operationType.loanDate)) / (1000 * 60 * 60 * 24))
      .filter((d) => !isNaN(d) && d >= 0);
    const avgRepaymentDays = repaymentDurations.length ? repaymentDurations.reduce((s, d) => s + d, 0) / repaymentDurations.length : null;
    return { outstandingDebt, recovered, overdue, avgRepaymentDays };
  }, [filteredCustomers, periodTransactions]);

  // deterministic rules reacting to every Executive Overview stat — same
  // "not AI" honesty as the Recommendations card in Operational Analytics
  const executiveTips = useMemo(() => {
    const tips = [];

    if (revenue > 0 && grossMargin < 20) {
      tips.push(`Gross margin is ${grossMargin.toFixed(1)}%, below a healthy 20–30% range — review cost prices or discounting.`);
    }
    if (orderStats.returned > 0) {
      tips.push(`${orderStats.returned} returned order${orderStats.returned === 1 ? "" : "s"} this period — check for a recurring product or quality issue.`);
    }
    if (customerStats.owingCount > 0) {
      tips.push(
        `${customerStats.owingCount} customer${customerStats.owingCount === 1 ? "" : "s"} owing money — following up could recover ${formatMoney(
          customerStats.owingTotal,
        )}.`,
      );
    }
    if (inventoryStats.outOfStock > 0) {
      tips.push(`${inventoryStats.outOfStock} store product${inventoryStats.outOfStock === 1 ? "" : "s"} out of stock — restock to avoid lost sales.`);
    }
    if (inventoryStats.lowStock > 0) {
      tips.push(`${inventoryStats.lowStock} store product${inventoryStats.lowStock === 1 ? "" : "s"} running low on stock.`);
    }
    if (creditStats.overdue > 0) {
      tips.push(`${creditStats.overdue} overdue loan${creditStats.overdue === 1 ? "" : "s"} — follow up before extending more credit.`);
    }
    if (warehouseStats.outOfStock > 0) {
      tips.push(
        `${warehouseStats.outOfStock} product${warehouseStats.outOfStock === 1 ? "" : "s"} out of stock in the warehouse — restock before stores run out too.`,
      );
    }
    if (warehouseStats.lowStock > 0) {
      tips.push(`${warehouseStats.lowStock} warehouse product${warehouseStats.lowStock === 1 ? "" : "s"} running low — worth reordering soon.`);
    }
    if (warehouseStats.damagedUnits > 0) {
      tips.push(
        `${warehouseStats.damagedUnits} damaged unit${
          warehouseStats.damagedUnits === 1 ? "" : "s"
        } sitting in the warehouse — worth checking storage or handling.`,
      );
    }
    if (warehouseStats.movedToStores > 0) {
      tips.push(
        `${warehouseStats.movedToStores} product${
          warehouseStats.movedToStores === 1 ? "" : "s"
        } moved to stores — confirm they were received and stocked correctly.`,
      );
    }
    if (warehouseStats.warehouseCount > 1) {
      tips.push(`Running ${warehouseStats.warehouseCount} warehouses — compare stock levels across them to catch imbalances early.`);
    }

    if (tips.length === 0) {
      tips.push("No issues detected across revenue, orders, customers, inventory, credit, or warehouse this period.");
    }
    return tips;
  }, [revenue, grossMargin, orderStats, customerStats, inventoryStats, creditStats, warehouseStats]);

  // ---------------- operational analytics ----------------

  /*  const salesByDay = useMemo(() => {
    const byDay = WEEKDAYS.map((label) => ({ label, value: 0 }));
    periodTransactions.forEach((t) => {
      const d = new Date(t.transactionDetails?.date);
      if (!isNaN(d)) byDay[d.getDay()].value += t.transactionDetails?.totalAmount || 0;
    });
    return byDay;
  }, [periodTransactions]); */

  const salesByDay = useMemo(() => {
    const byDay = WEEKDAYS.map((label) => ({
      label,
      value: 0,
    }));

    periodTransactions.forEach((t) => {
      if (isReturnedTransaction(t)) return;

      const d = new Date(t.transactionDetails?.date);

      if (!isNaN(d)) byDay[d.getDay()].value += getTransactionRevenue(t);
    });

    return byDay;
  }, [periodTransactions]);

  /*  const salesByHour = useMemo(() => {
    const byHour = HOUR_BUCKETS.map((h) => ({ label: `${h}:00`, value: 0 }));
    periodTransactions.forEach((t) => {
      const d = new Date(t.transactionDetails?.date);
      if (isNaN(d)) return;
      const hour = d.getHours();
      const bucketIndex = Math.min(Math.floor(hour / 2), HOUR_BUCKETS.length - 1);
      byHour[bucketIndex].value += t.transactionDetails?.totalAmount || 0;
    });
    return byHour;
  }, [periodTransactions]); */

  const salesByHour = useMemo(() => {
    const byHour = HOUR_BUCKETS.map((h) => ({
      label: `${h}:00`,
      value: 0,
    }));

    periodTransactions.forEach((t) => {
      if (isReturnedTransaction(t)) return;

      const d = new Date(t.transactionDetails?.date);

      if (isNaN(d)) return;

      const bucket = Math.min(Math.floor(d.getHours() / 2), HOUR_BUCKETS.length - 1);

      byHour[bucket].value += getTransactionRevenue(t);
    });

    return byHour;
  }, [periodTransactions]);

  /*   const monthlyComparison = useMemo(() => {
    const now = new Date();
    const sumForMonth = (year, month) =>
      transactions
        .filter((t) => {
          const d = new Date(t.transactionDetails?.date);
          return d.getFullYear() === year && d.getMonth() === month;
        })
        .reduce((s, t) => s + (t.transactionDetails?.totalAmount || 0), 0);

    return [
      { label: "This Month", value: sumForMonth(now.getFullYear(), now.getMonth()), color: "#1d6fd8" },
      {
        label: "Last Month",
        value: sumForMonth(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear(), now.getMonth() === 0 ? 11 : now.getMonth() - 1),
        color: "#37b4c5",
      },
      { label: "Same Month Last Year", value: sumForMonth(now.getFullYear() - 1, now.getMonth()), color: "#8a8f98" },
    ];
  }, [transactions]); */

  const monthlyComparison = useMemo(() => {
    const now = new Date();

    const sumForMonth = (year, month) =>
      transactions
        .filter((t) => {
          if (isReturnedTransaction(t)) return false;

          const d = new Date(t.transactionDetails?.date);

          return d.getFullYear() === year && d.getMonth() === month;
        })
        .reduce((sum, t) => sum + getTransactionRevenue(t), 0);

    return [
      {
        label: "This Month",
        value: sumForMonth(now.getFullYear(), now.getMonth()),
        color: "#1d6fd8",
      },
      {
        label: "Last Month",
        value: sumForMonth(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear(), now.getMonth() === 0 ? 11 : now.getMonth() - 1),
        color: "#37b4c5",
      },
      {
        label: "Same Month Last Year",
        value: sumForMonth(now.getFullYear() - 1, now.getMonth()),
        color: "#8a8f98",
      },
    ];
  }, [transactions]);

  const productPerformance = useMemo(() => {
    const map = new Map();
    periodTransactions.forEach((t) => {
      if (isReturnedTransaction(t)) return;
      (t.products || []).forEach((p) => {
        const key = String(p.id);
        const existing = map.get(key) || {
          productID: p.id,
          name: p.name,
          category: p.category,
          unitsSold: 0,
          revenue: 0,
          profit: 0,
        };
        existing.unitsSold += p.quantity || 0;
        existing.revenue += p.totalAmount || 0;
        existing.profit += (p.totalAmount || 0) - (p.productCostPrice || 0) * (p.quantity || 0);
        map.set(key, existing);
      });
    });
    const list = [...map.values()];
    const stockByID = new Map(filteredStoreProducts.map((p) => [String(p._id), p.quantity]));
    list.forEach((item) => {
      item.stockRemaining = stockByID.get(String(item.productID)) ?? "—";
    });
    return list.sort((a, b) => b.unitsSold - a.unitsSold);
  }, [periodTransactions, filteredStoreProducts]);

  const topSelling = productPerformance.slice(0, 8);

  const neverSoldProducts = useMemo(() => filteredStoreProducts.filter((p) => !(p.soldAmountAllTime > 0)).slice(0, 8), [filteredStoreProducts]);

  const categoryPerformance = useMemo(() => {
    const map = new Map();
    periodTransactions.forEach((t) => {
      if (isReturnedTransaction(t)) return;
      (t.products || []).forEach((p) => {
        const key = p.category || "Uncategorized";
        const existing = map.get(key) || { category: key, revenue: 0, profit: 0, items: 0 };
        existing.revenue += p.totalAmount || 0;
        existing.profit += (p.totalAmount || 0) - (p.productCostPrice || 0) * (p.quantity || 0);
        existing.items += p.quantity || 0;
        map.set(key, existing);
      });
    });
    return [...map.values()].sort((a, b) => b.revenue - a.revenue);
  }, [periodTransactions]);

  const brandPerformance = useMemo(() => {
    const map = new Map();
    filteredStoreProducts.forEach((p) => {
      const key = p.brand || "Unbranded";
      const existing = map.get(key) || { brand: key, revenue: 0, unitsSold: 0 };
      existing.unitsSold += p.soldAmountAllTime || 0;
      existing.revenue += (p.soldAmountAllTime || 0) * (p.unitPrice || 0);
      map.set(key, existing);
    });
    return [...map.values()].sort((a, b) => b.revenue - a.revenue);
  }, [filteredStoreProducts]);

  const damagedGoods = useMemo(() => {
    const damaged = filteredStoreProducts.filter((p) => (p.damagedAmount || 0) > 0);
    const totalDamagedUnits = damaged.reduce((s, p) => s + (p.damagedAmount || 0), 0);
    const totalDamagedCost = damaged.reduce((s, p) => s + (p.damagedAmount || 0) * (p.costPrice || 0), 0);
    return { list: damaged.sort((a, b) => b.damagedAmount - a.damagedAmount).slice(0, 5), totalDamagedUnits, totalDamagedCost };
  }, [filteredStoreProducts]);

  // employee metrics — derived from transactions.soldBy, no separate employees prop needed.
  // "voided transactions" and attendance aren't tracked in any schema shared, so omitted.
  /*   const employeeStats = useMemo(() => {
    const map = new Map();
    periodTransactions.forEach((t) => {
      const key = t.soldBy?.employeeID ? String(t.soldBy.employeeID) : t.soldBy?.fullName || "Unassigned";
      const existing = map.get(key) || { name: t.soldBy?.fullName || "Unassigned", sales: 0, revenue: 0, discounts: 0, returns: 0 };
      existing.returns += isReturnedTransaction(t) ? 1 : 0;
      existing.sales += 1;
      existing.revenue += getTransactionRevenue(t) || 0;

      existing.discounts += t.transactionDetails?.discount || 0;
      if (t.transactionDetails?.deliveryStatus === "returned") existing.returns += 1;
      map.set(key, existing);
    });
    return [...map.values()].map((e) => ({ ...e, avgTransaction: e.sales ? e.revenue / e.sales : 0 })).sort((a, b) => b.revenue - a.revenue);
  }, [periodTransactions]); */

  const employeeStats = useMemo(() => {
    const map = new Map();

    periodTransactions.forEach((t) => {
      const key = t.soldBy?.employeeID ? String(t.soldBy.employeeID) : t.soldBy?.fullName || "Unassigned";

      const existing = map.get(key) || {
        name: t.soldBy?.fullName || "Unassigned",
        sales: 0,
        revenue: 0,
        discounts: 0,
        returns: 0,
      };

      const returned = isReturnedTransaction(t);

      if (returned) {
        existing.returns += 1;
      } else {
        existing.sales += 1;
        existing.revenue += getTransactionRevenue(t);
        existing.discounts += t.transactionDetails?.discount || 0;
      }

      map.set(key, existing);
    });

    return [...map.values()]
      .map((employee) => ({
        ...employee,
        avgTransaction: employee.sales ? employee.revenue / employee.sales : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [periodTransactions]);

  // sales forecast — a plain linear-trend projection over the period's daily revenue.
  // NOT a real forecasting model (no seasonality, no ML) — a rough projection only.
  const salesForecast = useMemo(() => {
    const points = revenueTrend.map((p, i) => ({ x: i, y: p.value }));
    if (points.length < 2) return null;
    const n = points.length;
    const sumX = points.reduce((s, p) => s + p.x, 0);
    const sumY = points.reduce((s, p) => s + p.y, 0);
    const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
    const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);
    const denom = n * sumXX - sumX * sumX || 1;
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    const nextWeek = Array.from({ length: 7 }, (_, i) => Math.max(0, intercept + slope * (n + i)));
    const nextWeekTotal = nextWeek.reduce((s, v) => s + v, 0);
    const nextMonthTotal = nextWeekTotal * (30 / 7); // rough scale-up, not a separate projection
    return {
      slope,
      nextWeekTotal,
      nextMonthTotal,
      direction: slope > 0.5 ? "up" : slope < -0.5 ? "down" : "flat",
    };
  }, [revenueTrend]);

  // credit performance detail (beyond the executive mini-card) — top debtors and overdue list
  const creditPerformance = useMemo(() => {
    const topDebtors = [...filteredCustomers]
      .filter((c) => (c.amountOwned || 0) > 0)
      .sort((a, b) => (b.amountOwned || 0) - (a.amountOwned || 0))
      .slice(0, 6);
    const overdueLoans = periodTransactions
      .filter((t) => {
        if (t.operationType?.whichOperation !== "loan") return false;
        const dueDate = t.operationType?.dateToPay ? new Date(t.operationType.dateToPay) : null;
        return dueDate && dueDate < new Date() && (t.operationType?.balance || 0) > 0;
      })
      .slice(0, 6);
    const recoveryRate =
      creditStats.outstandingDebt + creditStats.recovered > 0 ? (creditStats.recovered / (creditStats.outstandingDebt + creditStats.recovered)) * 100 : 0;
    return { topDebtors, overdueLoans, recoveryRate };
  }, [filteredCustomers, periodTransactions, creditStats]);

  // business health score — a rule-based weighted score out of 100, NOT AI-generated.
  // Adjust the weights/thresholds directly here if they don't match how you'd judge health.
  const healthScore = useMemo(() => {
    const factors = [];

    const marginPts = Math.max(0, Math.min(25, (grossMargin / 40) * 25));
    factors.push({ label: "Gross margin", detail: `${grossMargin.toFixed(1)}% margin`, points: Math.round(marginPts), max: 25 });

    const trendPts = salesForecast ? (salesForecast.direction === "up" ? 20 : salesForecast.direction === "flat" ? 10 : 0) : 10;
    factors.push({
      label: "Revenue trend",
      detail: salesForecast ? `Trending ${salesForecast.direction}` : "Not enough data yet",
      points: trendPts,
      max: 20,
    });

    const stockRatio = inventoryStats.count > 0 ? inventoryStats.outOfStock / inventoryStats.count : 0;
    const stockPts = Math.max(0, Math.min(20, 20 - stockRatio * 100));
    factors.push({
      label: "Inventory availability",
      detail: `${inventoryStats.outOfStock} of ${inventoryStats.count} products out of stock`,
      points: Math.round(stockPts),
      max: 20,
    });

    const creditPts = Math.max(0, Math.min(20, 20 - creditStats.overdue * 4));
    factors.push({
      label: "Credit health",
      detail: `${creditStats.overdue} overdue loan${creditStats.overdue === 1 ? "" : "s"}`,
      points: Math.round(creditPts),
      max: 20,
    });

    const retentionBase = customerStats.returning + customerStats.newCustomers;
    const retentionRatio = retentionBase > 0 ? customerStats.returning / retentionBase : 0;
    const retentionPts = retentionRatio * 15;
    factors.push({
      label: "Customer retention",
      detail: `${Math.round(retentionRatio * 100)}% returning filteredCustomers`,
      points: Math.round(retentionPts),
      max: 15,
    });

    const total = factors.reduce((s, f) => s + f.points, 0);
    return { score: Math.round(Math.min(100, total)), factors };
  }, [grossMargin, salesForecast, inventoryStats, creditStats, customerStats]);

  // recommendations — deterministic rules reacting to the numbers above, NOT an LLM call.
  const recommendations = useMemo(() => {
    const recs = [];
    if (neverSoldProducts.length > 0) {
      recs.push(
        `${neverSoldProducts.length} product${neverSoldProducts.length === 1 ? "" : "s"} (e.g. "${neverSoldProducts[0].name}") ${
          neverSoldProducts.length === 1 ? "has" : "have"
        } never sold — consider a clearance discount or discontinuing.`,
      );
    }
    if (creditStats.overdue > 0) {
      recs.push(`${creditStats.overdue} loan${creditStats.overdue === 1 ? " is" : "s are"} overdue — follow up on repayment before extending further credit.`);
    }
    if (inventoryStats.outOfStock > 0) {
      recs.push(`${inventoryStats.outOfStock} product${inventoryStats.outOfStock === 1 ? "" : "s"} out of stock — restock to avoid lost sales.`);
    }
    if (revenue > 0 && grossMargin < 20) {
      recs.push(`Gross margin is ${grossMargin.toFixed(1)}%, below a healthy 20–30% range — review cost prices or discounting.`);
    }
    if (damagedGoods.totalDamagedUnits > 0) {
      recs.push(`${formatMoney(damagedGoods.totalDamagedCost)} lost to damaged stock — check handling and storage for the most-affected products.`);
    }
    if (salesForecast && salesForecast.direction === "down") {
      recs.push("Revenue is trending down over this period — review recent pricing, stock availability, or marketing activity.");
    }
    if (recs.length === 0) {
      recs.push("No urgent issues detected in this period — performance looks stable.");
    }
    return recs;
  }, [neverSoldProducts, creditStats, inventoryStats, revenue, grossMargin, damagedGoods, salesForecast]);

  // product price dynamics
  const priceDynamicsProducts = useMemo(() => {
    const map = new Map();
    priceDynamics.forEach((pd) => {
      const key = String(pd.productID);

      if (!map.has(key)) map.set(key, { productID: pd.productID, productName: pd.productName });
    });
    return [...map.values()];
  }, [priceDynamics]);

  const [selectedPriceProductID, setSelectedPriceProductID] = useState(null);
  const activePriceProductID = selectedPriceProductID || priceDynamicsProducts[0]?.productID || null;

  const priceChanges = useMemo(() => {
    const entries = priceDynamics.filter((pd) => String(pd.productID) === String(activePriceProductID));
    return entries.flatMap((e) => e.priceChanges.map((c) => ({ ...c }))).sort((a, b) => new Date(a.changedAt) - new Date(b.changedAt));
  }, [priceDynamics, activePriceProductID]);

  // ---------------- financial analytics ----------------

  const vatCollected = useMemo(() => periodTransactions.reduce((s, t) => s + (t.transactionDetails?.vat || 0), 0), [periodTransactions]);
  const discountGiven = useMemo(() => periodTransactions.reduce((s, t) => s + (t.transactionDetails?.discount || 0), 0), [periodTransactions]);
  const refundSummary = useMemo(
    () =>
      periodTransactions
        .filter((t) => isReturnedTransaction(t))
        .reduce((sum, t) => sum + ((t.transactionDetails?.subTotal || 0) - (t.transactionDetails?.discount || 0)), 0),
    [periodTransactions],
  );

  // ---------------- exports ----------------

  const getProfitAndLossRows = () => [
    { Line: "Revenue", Amount: revenue },
    { Line: "Cost of Goods Sold", Amount: -cogs },
    { Line: "Gross Profit", Amount: grossProfit },
    { Line: "Expenses", Amount: -totalExpenses },
    { Line: "Net Profit", Amount: netProfit },
  ];

  const getSalesReportRows = () =>
    periodTransactions.map((t) => ({
      Date: new Date(t.transactionDetails?.date).toLocaleString(),
      Customer: t.customer?.fullName || "—",
      Store: t.store?.name || "—",
      "Payment Mode": t.transactionDetails?.paymentMode,
      Items: t.transactionDetails?.numberOfItems,
      Subtotal: t.transactionDetails?.subTotal,
      VAT: t.transactionDetails?.vat,
      Discount: t.transactionDetails?.discount,
      Total: t.transactionDetails?.totalAmount,
      Status: t.transactionDetails?.deliveryStatus,
    }));

  const getInventoryValuationRows = () =>
    filteredStoreProducts.map((p) => ({
      Product: p.name,
      Category: p.productCategory,
      Quantity: p.quantity,
      "Cost Price": p.costPrice,
      "Selling Price": p.unitPrice,
      "Value (Cost)": (p.costPrice || 0) * (p.quantity || 0),
      "Value (Selling)": (p.unitPrice || 0) * (p.quantity || 0),
    }));

  // best-effort — your schema tracks current quantity, lifetime sold, and damaged amounts,
  // but not a running opening/closing ledger, so "movement" here is a snapshot, not a true ledger
  const getStockMovementRows = () =>
    filteredStoreProducts.map((p) => ({
      Product: p.name,
      "Current Quantity": p.quantity,
      "Sold All Time": p.soldAmountAllTime,
      "Sold Today": p.soldAmountToday,
      "Damaged Amount": p.damagedAmount,
      "Amount In Warehouse": p.amountInWarehouse,
    }));

  const getDebtorsReportRows = () => {
    const now = new Date();
    return filteredCustomers
      .filter((c) => (c.amountOwned || 0) > 0)
      .map((c) => {
        const daysSincePurchase = c.lastPurchaseDate ? Math.floor((now - new Date(c.lastPurchaseDate)) / (1000 * 60 * 60 * 24)) : null;
        return {
          Customer: c.fullName,
          Phone: c.phoneNumber,
          "Amount Owed": c.amountOwned,
          "Last Purchase": c.lastPurchaseDate ? new Date(c.lastPurchaseDate).toLocaleDateString() : "—",
          "Days Since Last Purchase": daysSincePurchase ?? "—",
        };
      });
  };

  const getTaxReportRows = () => [
    { Line: "VAT Collected", Amount: vatCollected },
    { Line: "Discount Given", Amount: discountGiven },
    { Line: "Refunds", Amount: refundSummary },
  ];

  const getTransactionReportRows = () =>
    periodTransactions.flatMap((t) =>
      (t.products || []).map((p) => ({
        Date: new Date(t.transactionDetails?.date).toLocaleString(),
        TransactionID: t.transID,
        Product: p.name,
        Quantity: p.quantity,
        "Unit Price": p.soldUnitPrice,
        "Line Total": p.totalAmount,
        "Payment Mode": t.transactionDetails?.paymentMode,
        Cashier: t.soldBy?.fullName || "—",
        Customer: t.customer?.fullName || "—",
        Store: t.store?.name || "—",
        "Delivery Status": t.transactionDetails?.deliveryStatus,
      })),
    );

  const REPORT_DEFINITIONS = [
    {
      key: "pnl",
      title: "Profit & Loss Statement",
      description: "Revenue, COGS, gross profit, expenses, net profit",
      sheetName: "P&L",
      getRows: getProfitAndLossRows,
    },
    { key: "sales", title: "Sales Report", description: "Every transaction in the selected period", sheetName: "Sales", getRows: getSalesReportRows },
    {
      key: "inventory",
      title: "Inventory Valuation",
      description: "Cost and selling value per product",
      sheetName: "Inventory Valuation",
      getRows: getInventoryValuationRows,
    },
    {
      key: "stock",
      title: "Stock Movement",
      description: "Current quantity, lifetime sold, damaged (snapshot, not a full ledger)",
      sheetName: "Stock Movement",
      getRows: getStockMovementRows,
    },
    { key: "debtors", title: "Debtors Aging", description: "Customers with an outstanding balance", sheetName: "Debtors", getRows: getDebtorsReportRows },
    { key: "tax", title: "Tax Summary", description: "VAT collected, discounts given, refunds", sheetName: "Tax", getRows: getTaxReportRows },
    {
      key: "transactions",
      title: "Transaction Detail",
      description: "Line-item breakdown of every sale",
      sheetName: "Transactions",
      getRows: getTransactionReportRows,
    },
  ];

  const [selectedSections, setSelectedSections] = useState(Object.fromEntries(REPORT_DEFINITIONS.map((r) => [r.key, true])));

  const toggleSection = (key) => setSelectedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedCount = Object.values(selectedSections).filter(Boolean).length;

  const exportCombinedReport = () => {
    const workbook = XLSX.utils.book_new();
    let sheetsAdded = 0;

    REPORT_DEFINITIONS.forEach((def) => {
      if (!selectedSections[def.key]) return;
      const rows = def.getRows();
      if (!rows.length) return;
      const worksheet = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(workbook, worksheet, def.sheetName.slice(0, 31)); // Excel sheet name limit
      sheetsAdded++;
    });

    if (sheetsAdded === 0) return;

    const presetLabel = DATE_PRESETS.find((p) => p.key === datePreset)?.label || "report";
    XLSX.writeFile(workbook, `business-report-${presetLabel.replace(/\s+/g, "-").toLowerCase()}.xlsx`);
    setIsReportSheetOpen(false);
  };

  const currentDateLabel = DATE_PRESETS.find((p) => p.key === datePreset)?.label || "Timeline";

  const renderTimelineOptions = (onPick) => (
    <>
      <div className='sl-ar-date-chips'>
        {DATE_PRESETS.map(({ key, label }) => (
          <button
            key={key}
            className={"sl-ar-date-chip" + (datePreset === key ? " sl-ar-date-chip--active" : "")}
            onClick={() => {
              setDatePreset(key);
              if (key !== "custom") onPick && onPick();
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {datePreset === "custom" && (
        <div className='sl-ar-custom-range'>
          <input type='date' value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
          <span>to</span>
          <input type='date' value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
        </div>
      )}
    </>
  );

  const filterBar = (
    <div className='sl-ar-filter-bar'>
      <button className='sl-ar-timeline-trigger' onClick={() => setIsTimelineSheetOpen(true)}>
        <Calendar size={14} />
        <span>{currentDateLabel}</span>
        <ChevronDown size={13} />
      </button>
      <StoreDropdown storeFilter={storeFilter} setStoreFilter={setStoreFilter} availableStores={availableStores} />
      {activeTab === "executive" && (
        <button className='sl-ar-insights-trigger' onClick={() => setIsInsightsSheetOpen(true)} aria-label='View insights'>
          <Gauge size={14} />
          <span>Insights</span>
        </button>
      )}
    </div>
  );

  const renderKpiRow = () => (
    <>
      <div className='sl-ar-kpi sl-ar-kpi--primary'>
        <span className='sl-ar-kpi-label'>Revenue</span>
        <span className='sl-ar-kpi-value'>{formatMoney(revenue)}</span>
      </div>
      <div className='sl-ar-kpi'>
        <span className='sl-ar-kpi-label'>Gross Profit</span>
        <span className='sl-ar-kpi-value'>{formatMoney(grossProfit)}</span>
        <span className='sl-ar-kpi-sub'>{grossMargin.toFixed(1)}% margin</span>
      </div>
      <div className='sl-ar-kpi'>
        <span className='sl-ar-kpi-label'>Net Profit</span>
        <span className='sl-ar-kpi-value' style={{ color: netProfit >= 0 ? "#1f9d55" : "#c23a3a" }}>
          {formatMoney(netProfit)}
        </span>
        <span className='sl-ar-kpi-sub'>after {formatMoney(totalExpenses)} expenses</span>
      </div>
    </>
  );

  if (isLoading && !hasLoadedOnce) {
    return (
      <Template>
        <div className='sl-ar-loading'>
          <Spinner position={""} />
          <p>{loadingProgress}</p>
        </div>
      </Template>
    );
  }

  return (
    <Template>
      <div className='sl-ar'>
        {isLoading && hasLoadedOnce && (
          <div className='sl-ar-refresh-bar'>
            <span className='sl-ar-refresh-dot' />
            Updating data...
          </div>
        )}
        <div className='sl-ar-header'>
          <div>
            <h2>Analytics & Reports</h2>
            <p>Business performance across payouts, operations, finances and downloadable statements</p>
          </div>
          <button className='sl-ar-download-report-btn' onClick={() => setIsReportSheetOpen(true)}>
            <Download size={15} />
            <span>Download Report</span>
          </button>
        </div>

        <div className='sl-ar-tabs'>
          {[
            { key: "executive", label: "Executive Overview", shortLabel: "Overview", icon: LayoutDashboard },
            { key: "operational", label: "Operational Analytics", shortLabel: "Operational", icon: Activity },
            { key: "financial", label: "Financial Analytics", shortLabel: "Financial", icon: Wallet },
          ].map(({ key, label, shortLabel, icon: Icon }) => (
            <button key={key} className={"sl-ar-tab" + (activeTab === key ? " sl-ar-tab--active" : "")} onClick={() => setActiveTab(key)}>
              <Icon size={15} />
              <span className='sl-ar-tab-label-full'>{label}</span>
              <span className='sl-ar-tab-label-short'>{shortLabel}</span>
            </button>
          ))}
        </div>

        {filterBar}

        {activeTab === "executive" && (
          <div className='sl-ar-panel'>
            <div className='sl-ar-kpi-row sl-ar-kpi-row--desktop'>{renderKpiRow()}</div>

            {/*  <div className='sl-ar-grid-2'>
              <div className='sl-ar-card'>
                <h4>
                  <TrendingUp size={14} /> Revenue Trend
                </h4>
                {revenueTrend.length ? <MiniLineChart points={revenueTrend} color='#1d6fd8' /> : <p className='sl-ar-empty'>No revenue in this period.</p>}
              </div>
              <div className='sl-ar-card'>
                <h4>
                  <TrendingUp size={14} /> Profit Trend
                </h4>
                {profitTrend.length ? <MiniLineChart points={profitTrend} color='#1f9d55' /> : <p className='sl-ar-empty'>No profit data in this period.</p>}
              </div>
            </div> */}
            <div className='sl-ar-grid-2'>
              <div className='sl-ar-card'>
                <div className='sl-ar-card-header-row'>
                  <h4>
                    <TrendingUp size={14} /> Revenue Trend
                  </h4>
                  {revenueTrend.length > 0 && <span className='sl-ar-avg-badge'>Avg {formatMoney(avgRevenue)}/calendar day</span>}
                </div>
                {revenueTrend.length ? (
                  <MiniLineChart points={revenueTrend} color='#1d6fd8' average={avgRevenue} />
                ) : (
                  <p className='sl-ar-empty'>No revenue in this period.</p>
                )}
              </div>
              <div className='sl-ar-card'>
                <div className='sl-ar-card-header-row'>
                  <h4>
                    <TrendingUp size={14} /> Profit Trend
                  </h4>
                  {profitTrend.length > 0 && <span className='sl-ar-avg-badge'>Avg {formatMoney(avgProfit)}/calendar day</span>}
                </div>
                {profitTrend.length ? (
                  <MiniLineChart points={profitTrend} color='#1f9d55' average={avgProfit} />
                ) : (
                  <p className='sl-ar-empty'>No profit data in this period.</p>
                )}
              </div>
            </div>

            <div className='sl-ar-card'>
              <h4>
                <Wallet size={14} /> Cash Flow Channels
              </h4>
              <MiniDonut segments={cashFlowSegments} currency={currency} />
            </div>

            <div className='sl-ar-executive-grid'>
              <div className='sl-ar-mini-card'>
                <ShoppingBag size={16} />
                <span className='sl-ar-mini-title'>Orders</span>
                <div className='sl-ar-mini-row'>
                  <span>Transactions</span>
                  <b>{orderStats.count.toLocaleString()}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Avg. Order Value</span>
                  <b>{formatMoney(orderStats.aov)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Highest Sale</span>
                  <b>{formatMoney(orderStats.highest)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Lowest Sale</span>
                  <b>{formatMoney(orderStats.lowest)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Returned Orders</span>
                  <b>{orderStats.returned}</b>
                </div>
              </div>

              <div className='sl-ar-mini-card'>
                <Users size={16} />
                <span className='sl-ar-mini-title'>Customers</span>
                <div className='sl-ar-mini-row'>
                  <span>New</span>
                  <b>{customerStats.newCustomers}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Returning</span>
                  <b>{customerStats.returning}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Active</span>
                  <b>{customerStats.active}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Top Customer</span>
                  <b>{customerStats.topCustomerName}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Owing</span>
                  <b>{customerStats.owingCount}</b>
                </div>
              </div>

              <div className='sl-ar-mini-card'>
                <Boxes size={16} />
                <span className='sl-ar-mini-title'>Inventory</span>
                <div className='sl-ar-mini-row'>
                  <span>Value (Cost)</span>
                  <b>{formatCompact(inventoryStats.valueCost)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Value (Selling)</span>
                  <b>{formatCompact(inventoryStats.valueSelling)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Products</span>
                  <b>{inventoryStats.count}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Low Stock</span>
                  <b>{inventoryStats.lowStock}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Out of Stock</span>
                  <b>{inventoryStats.outOfStock}</b>
                </div>
              </div>

              <div className='sl-ar-mini-card'>
                <CreditCard size={16} />
                <span className='sl-ar-mini-title'>Credit</span>
                <div className='sl-ar-mini-row'>
                  <span>Outstanding Debt</span>
                  <b>{formatCompact(creditStats.outstandingDebt)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Recovered</span>
                  <b>{formatCompact(creditStats.recovered)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Overdue Loans</span>
                  <b>{creditStats.overdue}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Avg. Repayment</span>
                  <b>{creditStats.avgRepaymentDays !== null ? `${Math.round(creditStats.avgRepaymentDays)}d` : "—"}</b>
                </div>
              </div>

              <div className='sl-ar-mini-card sl-ar-mini-card--highlight'>
                <Warehouse size={16} />
                <span className='sl-ar-mini-title'>Warehouse</span>
                <div className='sl-ar-mini-row sl-ar-mini-row--highlight'>
                  <span>Products in Warehouse</span>
                  <b>{warehouseStats.productCount.toLocaleString()}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Warehouses</span>
                  <b>{warehouseStats.warehouseCount}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Total Units</span>
                  <b>{warehouseStats.totalUnits.toLocaleString()}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Value (Cost)</span>
                  <b>{formatCompact(warehouseStats.valueCost)}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Low Stock</span>
                  <b>{warehouseStats.lowStock}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Out of Stock</span>
                  <b>{warehouseStats.outOfStock}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Damaged Units</span>
                  <b>{warehouseStats.damagedUnits}</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Moved to Stores</span>
                  <b>{warehouseStats.movedToStores}</b>
                </div>
              </div>
            </div>
            <div className='sl-ar-card sl-ar-tips-card'>
              <h4>
                <Lightbulb size={14} /> Tips
              </h4>
              <div className='sl-ar-tip-grid'>
                {executiveTips.map((tip, i) => (
                  <div className='sl-ar-tip-row' key={i}>
                    {tip}
                  </div>
                ))}
              </div>
              <p className='sl-ar-note'>
                These figures and tips are only as accurate as the data your team has entered — Analytics &amp; Reports doesn't verify what's recorded, it just
                calculates from it. In particular, make sure every product's cost price is entered correctly: profit, margin, and inventory valuation across
                every section above are all derived directly from it, so an inaccurate cost price will throw off every number that depends on it.
              </p>
            </div>
          </div>
        )}

        {activeTab === "operational" && (
          <div className='sl-ar-panel'>
            <div className='sl-ar-grid-2'>
              <div className='sl-ar-card'>
                <h4>Sales by Day</h4>
                <MiniBarChart bars={salesByDay} color='#1d6fd8' />
              </div>
              <div className='sl-ar-card'>
                <h4>Sales by Hour</h4>
                <MiniBarChart bars={salesByHour} color='#37b4c5' />
              </div>
            </div>

            <div className='sl-ar-card'>
              <h4>Monthly Sales Comparison</h4>
              <MiniBarChart bars={monthlyComparison} />
            </div>

            <div className='sl-ar-grid-2'>
              <div className='sl-ar-card'>
                <h4>Top Selling Products</h4>
                <table className='sl-ar-table'>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Units</th>
                      <th>Revenue</th>
                      <th>Profit</th>
                      <th>Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSelling.length === 0 ? (
                      <tr>
                        <td colSpan={5} className='sl-ar-table-empty'>
                          No sales in this period.
                        </td>
                      </tr>
                    ) : (
                      topSelling.map((p) => (
                        <tr key={p.productID}>
                          <td>{p.name}</td>
                          <td>{p.unitsSold}</td>
                          <td>{formatMoney(p.revenue)}</td>
                          <td>{formatMoney(p.profit)}</td>
                          <td>{p.stockRemaining}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <div className='sl-ar-mobile-cards'>
                  {topSelling.length === 0 ? (
                    <p className='sl-ar-empty'>No sales in this period.</p>
                  ) : (
                    topSelling.map((p) => (
                      <DataCard
                        key={p.productID}
                        title={p.name}
                        badge={`${p.unitsSold} units`}
                        rows={[
                          ["Revenue", formatMoney(p.revenue)],
                          ["Profit", formatMoney(p.profit)],
                          ["Stock", p.stockRemaining],
                        ]}
                      />
                    ))
                  )}
                </div>
              </div>

              <div className='sl-ar-card'>
                <h4>
                  <AlertTriangle size={14} /> Never Sold / Dead Stock
                </h4>
                {neverSoldProducts.length === 0 ? (
                  <p className='sl-ar-empty'>Nothing sitting unsold right now.</p>
                ) : (
                  neverSoldProducts.map((p) => (
                    <div className='sl-ar-list-row' key={p._id}>
                      <span>{p.name}</span>
                      <span className='sl-ar-list-row-muted'>{p.quantity} in stock</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className='sl-ar-grid-2'>
              <div className='sl-ar-card'>
                <h4>Category Performance</h4>
                <table className='sl-ar-table'>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Revenue</th>
                      <th>Profit</th>
                      <th>Items Sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryPerformance.map((c) => (
                      <tr key={c.category}>
                        <td>{c.category}</td>
                        <td>{formatMoney(c.revenue)}</td>
                        <td>{formatMoney(c.profit)}</td>
                        <td>{c.items}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className='sl-ar-mobile-cards'>
                  {categoryPerformance.length === 0 ? (
                    <p className='sl-ar-empty'>No category data yet.</p>
                  ) : (
                    categoryPerformance.map((c) => (
                      <DataCard
                        key={c.category}
                        title={c.category}
                        rows={[
                          ["Revenue", formatMoney(c.revenue)],
                          ["Profit", formatMoney(c.profit)],
                          ["Items Sold", c.items],
                        ]}
                      />
                    ))
                  )}
                </div>
              </div>

              <div className='sl-ar-card'>
                <h4>Brand Performance</h4>
                <table className='sl-ar-table'>
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>Units Sold (all time)</th>
                      <th>Est. Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brandPerformance.map((b) => (
                      <tr key={b.brand}>
                        <td>{b.brand}</td>
                        <td>{b.unitsSold}</td>
                        <td>{formatMoney(b.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className='sl-ar-mobile-cards'>
                  {brandPerformance.length === 0 ? (
                    <p className='sl-ar-empty'>No brand data yet.</p>
                  ) : (
                    brandPerformance.map((b) => (
                      <DataCard
                        key={b.brand}
                        title={b.brand}
                        rows={[
                          ["Units Sold (all time)", b.unitsSold],
                          ["Est. Revenue", formatMoney(b.revenue)],
                        ]}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className='sl-ar-card'>
              <h4>
                <AlertTriangle size={14} /> Damaged Goods
              </h4>
              <div className='sl-ar-mini-row'>
                <span>Total Damaged Units</span>
                <b>{damagedGoods.totalDamagedUnits}</b>
              </div>
              <div className='sl-ar-mini-row'>
                <span>Cost of Damage</span>
                <b>{formatMoney(damagedGoods.totalDamagedCost)}</b>
              </div>
              {damagedGoods.list.map((p) => (
                <div className='sl-ar-list-row' key={p._id}>
                  <span>{p.name}</span>
                  <span className='sl-ar-list-row-muted'>{p.damagedAmount} damaged</span>
                </div>
              ))}
            </div>

            <div className='sl-ar-card'>
              <h4>
                <UserCheck size={14} /> Employee Leaderboard
              </h4>
              <table className='sl-ar-table'>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Sales</th>
                    <th>Revenue</th>
                    <th>Avg. Transaction</th>
                    <th>Discounts</th>
                    <th>Returns</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeStats.length === 0 ? (
                    <tr>
                      <td colSpan={6} className='sl-ar-table-empty'>
                        No sales attributed to an employee in this period.
                      </td>
                    </tr>
                  ) : (
                    employeeStats.map((e) => (
                      <tr key={e.name}>
                        <td>{e.name}</td>
                        <td>{e.sales}</td>
                        <td>{formatMoney(e.revenue)}</td>
                        <td>{formatMoney(e.avgTransaction)}</td>
                        <td>{formatMoney(e.discounts)}</td>
                        <td>{e.returns}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className='sl-ar-mobile-cards'>
                {employeeStats.length === 0 ? (
                  <p className='sl-ar-empty'>No sales attributed to an employee in this period.</p>
                ) : (
                  employeeStats.map((e) => (
                    <DataCard
                      key={e.name}
                      title={e.name}
                      badge={`${e.sales} sales`}
                      rows={[
                        ["Revenue", formatMoney(e.revenue)],
                        ["Avg. Transaction", formatMoney(e.avgTransaction)],
                        ["Discounts", formatMoney(e.discounts)],
                        ["Returns", e.returns],
                      ]}
                    />
                  ))
                )}
              </div>
            </div>

            <div className='sl-ar-grid-2'>
              <div className='sl-ar-card'>
                <h4>
                  <LineChart size={14} /> Sales Forecast
                </h4>
                {salesForecast ? (
                  <>
                    <div className='sl-ar-mini-row'>
                      <span>Trend</span>
                      <b style={{ textTransform: "capitalize" }}>{salesForecast.direction}</b>
                    </div>
                    <div className='sl-ar-mini-row'>
                      <span>Expected Next 7 Days</span>
                      <b>{formatMoney(salesForecast.nextWeekTotal)}</b>
                    </div>
                    <div className='sl-ar-mini-row'>
                      <span>Expected Next 30 Days</span>
                      <b>{formatMoney(salesForecast.nextMonthTotal)}</b>
                    </div>
                    <p className='sl-ar-note' style={{ marginTop: 10 }}>
                      Simple linear projection off this period's daily revenue — not a seasonal or ML forecast.
                    </p>
                  </>
                ) : (
                  <p className='sl-ar-empty'>Not enough data points in this period to project a trend.</p>
                )}
              </div>

              <div className='sl-ar-card'>
                <h4>
                  <CreditCard size={14} /> Credit Performance
                </h4>
                <div className='sl-ar-mini-row'>
                  <span>Recovery Rate</span>
                  <b>{creditPerformance.recoveryRate.toFixed(1)}%</b>
                </div>
                <div className='sl-ar-mini-row'>
                  <span>Overdue Loans</span>
                  <b>{creditPerformance.overdueLoans.length}</b>
                </div>
                {creditPerformance.topDebtors.length > 0 && (
                  <>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#8a8f98", textTransform: "uppercase", letterSpacing: "0.04em", margin: "10px 0 4px" }}>
                      Top Debtors
                    </p>
                    {creditPerformance.topDebtors.map((c) => (
                      <div className='sl-ar-list-row' key={c._id || c.customerID}>
                        <span>{c.fullName}</span>
                        <span className='sl-ar-list-row-muted'>{formatMoney(c.amountOwned)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            <div className='sl-ar-grid-2'>
              <div className='sl-ar-card'>
                <h4>
                  <Gauge size={14} /> Business Health Score
                </h4>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
                  <span
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 32,
                      fontWeight: 700,
                      color: healthScore.score >= 70 ? "#1f9d55" : healthScore.score >= 45 ? "#a68b00" : "#c23a3a",
                    }}
                  >
                    {healthScore.score}
                  </span>
                  <span style={{ fontSize: 13, color: "#8a8f98" }}>/ 100</span>
                </div>
                {healthScore.factors.map((f) => (
                  <div key={f.label} style={{ marginBottom: 8 }}>
                    <div className='sl-ar-mini-row'>
                      <span>{f.label}</span>
                      <b>
                        {f.points}/{f.max}
                      </b>
                    </div>
                    <div style={{ height: 5, background: "#edece6", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(f.points / f.max) * 100}%`, background: "#1d6fd8", borderRadius: 3 }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#8a8f98", marginTop: 2 }}>{f.detail}</div>
                  </div>
                ))}
                <p className='sl-ar-note'>Rule-based weighted score, the rules are fixed based on an average KPI.</p>
              </div>

              <div className='sl-ar-card'>
                <h4>
                  <Lightbulb size={14} /> Recommendations
                </h4>
                {recommendations.map((r, i) => (
                  <div className='sl-ar-list-row' key={i} style={{ alignItems: "flex-start" }}>
                    <span style={{ fontWeight: 500 }}>{r}</span>
                  </div>
                ))}
                <p className='sl-ar-note'>Deterministic rules reacting to the numbers above, contact support if wish for a detail recommendations.</p>
              </div>
            </div>

            <div className='sl-ar-card'>
              <h4>
                <DollarSign size={14} /> Product Price Dynamics
              </h4>
              {priceDynamicsProducts.length === 0 ? (
                <p className='sl-ar-empty'>No price change history available.</p>
              ) : (
                <>
                  <select
                    value={activePriceProductID || ""}
                    onChange={(e) => setSelectedPriceProductID(e.target.value)}
                    style={{
                      marginBottom: 12,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #d7dae0",
                      fontSize: 12.5,
                      fontFamily: "'Nunito', sans-serif",
                    }}
                  >
                    {priceDynamicsProducts.map((p) => (
                      <option key={p.productID} value={p.productID}>
                        {p.productName}
                      </option>
                    ))}
                  </select>
                  <PriceDynamicsChart changes={priceChanges} currency={currency} />
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === "financial" && (
          <div className='sl-ar-panel'>
            <div className='sl-ar-card'>
              <h4>Revenue vs Profit vs Expenses</h4>
              <MiniBarChart
                showValues
                bars={[
                  { label: "Revenue", value: revenue, color: "#1d6fd8" },
                  { label: "Gross Profit", value: grossProfit, color: "#1f9d55" },
                  { label: "Expenses", value: totalExpenses, color: "#c23a3a" },
                  { label: "Net Profit", value: netProfit, color: "#37b4c5" },
                ]}
              />
            </div>

            <div className='sl-ar-card'>
              <h4>Cash Flow</h4>
              <MiniDonut segments={cashFlowSegments} currency={currency} />
            </div>

            <div className='sl-ar-grid-4'>
              <div className='sl-ar-kpi'>
                <span className='sl-ar-kpi-label'>Outstanding Receivables</span>
                <span className='sl-ar-kpi-value'>{formatMoney(creditStats.outstandingDebt)}</span>
              </div>
              <div className='sl-ar-kpi'>
                <span className='sl-ar-kpi-label'>VAT Collected</span>
                <span className='sl-ar-kpi-value'>{formatMoney(vatCollected)}</span>
              </div>
              <div className='sl-ar-kpi'>
                <span className='sl-ar-kpi-label'>Discount Given</span>
                <span className='sl-ar-kpi-value'>{formatMoney(discountGiven)}</span>
              </div>
              <div className='sl-ar-kpi'>
                <span className='sl-ar-kpi-label'>Refunds</span>
                <span className='sl-ar-kpi-value'>{formatMoney(refundSummary)}</span>
              </div>
            </div>

            <p className='sl-ar-note'>
              Income tax isn't computed here — a real income tax figure needs full accounting context (allowable deductions, prior losses, statutory rates)
              beyond what your app can track. VAT collected is the reliable number available; the full Profit; Loss statement under Downloadable Reports is the
              better input to hand to an accountant for an actual tax filing.
            </p>
          </div>
        )}

        {isTimelineSheetOpen && (
          <div className='sl-ar-sheet-overlay' onClick={() => setIsTimelineSheetOpen(false)}>
            <div className='sl-ar-sheet' onClick={(e) => e.stopPropagation()}>
              <div className='sl-ar-sheet-handle' />
              <div className='sl-ar-sheet-header'>
                <h4>Select timeline</h4>
                <button onClick={() => setIsTimelineSheetOpen(false)}>
                  <X size={16} />
                </button>
              </div>
              <div className='sl-ar-sheet-body'>{renderTimelineOptions(() => setIsTimelineSheetOpen(false))}</div>
              {datePreset === "custom" && (
                <div className='sl-ar-sheet-footer'>
                  <button className='sl-ar-sheet-apply-btn' onClick={() => setIsTimelineSheetOpen(false)}>
                    Apply
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {isReportSheetOpen && (
          <div className='sl-ar-sheet-overlay' onClick={() => setIsReportSheetOpen(false)}>
            <div className='sl-ar-sheet' onClick={(e) => e.stopPropagation()}>
              <div className='sl-ar-sheet-handle' />
              <div className='sl-ar-sheet-header'>
                <h4>Choose report sections (Excel format) </h4>
                <button onClick={() => setIsReportSheetOpen(false)}>
                  <X size={16} />
                </button>
              </div>
              <div className='sl-ar-sheet-body'>
                {REPORT_DEFINITIONS.map((def) => (
                  <label className='sl-ar-checkbox-row' key={def.key}>
                    <input type='checkbox' checked={!!selectedSections[def.key]} onChange={() => toggleSection(def.key)} />
                    <span>
                      <span className='sl-ar-checkbox-title'>{def.title}</span>
                      <span className='sl-ar-checkbox-description'>{def.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className='sl-ar-sheet-footer'>
                <button className='sl-ar-sheet-apply-btn' onClick={exportCombinedReport} disabled={selectedCount === 0}>
                  <Download size={14} />
                  Download {selectedCount} section{selectedCount === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isInsightsSheetOpen && (
          <div className='sl-ar-sheet-overlay' onClick={() => setIsInsightsSheetOpen(false)}>
            <div className='sl-ar-sheet' onClick={(e) => e.stopPropagation()}>
              <div className='sl-ar-sheet-handle' />
              <div className='sl-ar-sheet-header'>
                <h4>Insights</h4>
                <button onClick={() => setIsInsightsSheetOpen(false)}>
                  <X size={16} />
                </button>
              </div>
              <div className='sl-ar-sheet-body'>
                <div className='sl-ar-kpi-row sl-ar-kpi-row--sheet'>{renderKpiRow()}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Template>
  );
};
